// The image viewer's view of a media ref, and the pure rules behind it: what may load, which
// size to load, and how gestures settle (DES2-image-ux.md §A). No React, no DOM.
//
// ARCH-MANAGED-IMAGES: the viewer shows a current authorized ManagedImageProjection, mapped by
// the one adapter below (fromManagedImage). It never sees a retailer URL, object key or lease:
// only same-origin variant routes the projection already carries.
import type { MediaCopyKey } from '@/i18n/ns/media';
import type { ManagedImageProjection } from '../../../src/artifacts/images/projection.mjs';
import type { ImageRatio } from '../../../src/artifacts/images/values.mjs';

/** ARCH's projection states. Browser byte loading is the viewer's own local state. */
export type MediaState = 'ready' | 'capturing' | 'missing' | 'expired' | 'withdrawn' | 'refused';
/** What the viewer shows: a projection state, or `loading` while ready bytes arrive. */
export type MediaView = MediaState | 'loading';
export type MediaSize = { src: string; width: number };
export type MediaItem = {
  id: string;
  state: MediaState;
  /** From the data ("Wool cap, product photo"), never "image". */
  alt: string;
  /** Same-origin responsive sizes, any order. */
  sizes?: readonly MediaSize[];
  /** The source's name for the provenance line ("Shop"): the projection's sourceLabel. */
  source?: string | null;
  /** When the source observed it (ISO): the projection's observedAt. */
  seenAt?: string | null;
  /** In History: today's image differs from the one observed at this version. */
  changedSince?: boolean;
  /** The declared ratio ("4:5", "1:1", "16:9"); a placeholder keeps it. */
  ratio?: string | null;
};

/** "4:5" → "4 / 5" for aspect-ratio; anything else → null (the square fallback). */
export function cssRatio(ratio: string | null | undefined): string | null {
  const m = /^(\d{1,5}):(\d{1,5})$/u.exec(ratio ?? '');
  return m && Number(m[1]) > 0 && Number(m[2]) > 0 ? `${m[1]} / ${m[2]}` : null;
}

export const ZOOM_MAX = 4;
export const ZOOM_DOUBLE = 2.5;
/** A swipe past this share of the width, or a flick, moves to the next item. */
export const NAV_SHARE = 0.25;
/** A pull down past this, or a flick, dismisses (phone, at 1×). */
export const DISMISS_PX = 120;
/** px per ms that counts as a flick. */
export const FLICK = 0.5;
/** How much the ends give when there is no next item. */
export const EDGE_RESISTANCE = 0.3;

/** INS's current browser projection (src/artifacts/images, #3344); the viewer's only input. */
export type { ManagedImageProjection };

/**
 * The one adapter from INS's ManagedImageProjection to the viewer. Ready variants (master and
 * the 320/640/1280 renditions) keep only RUN-issued same-origin `src`s, at their actual pixel
 * width; a ready item left with none reads as refused. Expired and withdrawn carry no pixels.
 * `ratio` comes from the field or slot metadata (`intrinsic` uses the image's own shape),
 * `changedSince` from History. Nothing else is inferred.
 */
export function fromManagedImage(
  p: ManagedImageProjection,
  extra: { ratio?: ImageRatio | null; changedSince?: boolean; key?: string } = {},
): MediaItem {
  const ready = p.state === 'ready' ? p : null;
  const id =
    extra.key ??
    ('imageId' in p ? p.imageId : 'captureOperationRef' in p ? p.captureOperationRef : '');
  const ratio =
    extra.ratio === 'intrinsic'
      ? ready
        ? `${ready.width}:${ready.height}`
        : null
      : (extra.ratio ?? null);
  return {
    id,
    state: p.state,
    alt: p.alt,
    sizes: ready
      ? ready.variants.filter((v) => safeSrc(v.src)).map((v) => ({ src: v.src, width: v.width }))
      : [],
    source: p.sourceLabel || null,
    seenAt: p.observedAt,
    ratio,
    changedSince: extra.changedSince ?? false,
  };
}

// A sentinel origin no real request can have: a path resolved against it must keep it.
const SAME = 'https://same-origin.invalid';
// The URL parser strips tab, CR and LF anywhere and trims C0 controls and spaces at the ends, so
// "/\n/host/x" would become "//host/x" (another origin). Any of them, or a backslash (which the
// parser reads as "/"), refuses the source outright.
const UNSAFE = /[\u0000-\u0020\u007f\\]/u;

/**
 * Only same-origin paths ever load (ARCH-MANAGED-IMAGES: no external, presigned or data URLs).
 * The check is the parser's own: the path, resolved against a sentinel origin, must keep that
 * origin. A full URL, a protocol-relative URL or anything a separator trick turns into one is
 * refused, never fetched. A safe source is not authorization: the managed-ref adapter decides
 * which sources a person may see.
 */
export function safeSrc(src: string): boolean {
  if (typeof src !== 'string' || !src || UNSAFE.test(src)) return false;
  if (!src.startsWith('/') || src.startsWith('//')) return false;
  try {
    const url = new URL(src, SAME);
    return url.origin === SAME && url.pathname.startsWith('/') && !url.pathname.startsWith('//');
  } catch {
    return false;
  }
}

/** The item as shown: ready only when it has at least one size that may load. */
export function shownState(item: MediaItem): MediaState {
  if (item.state !== 'ready') return item.state;
  return (item.sizes ?? []).some((s) => safeSrc(s.src)) ? 'ready' : 'refused';
}

/** The smallest allowed size at least `need` px wide, else the largest there is. */
export function pickSize(item: MediaItem, need: number): MediaSize | null {
  const sizes = (item.sizes ?? []).filter((s) => safeSrc(s.src)).sort((a, b) => a.width - b.width);
  return sizes.find((s) => s.width >= need) ?? sizes.at(-1) ?? null;
}

/** The smallest allowed size: what a thumbnail already loaded, so the viewer opens at once. */
export const thumbSize = (item: MediaItem) => pickSize(item, 0);

export const STATE_COPY: Record<Exclude<MediaView, 'ready'>, MediaCopyKey> = {
  loading: 'loading',
  capturing: 'loading',
  missing: 'missing',
  expired: 'expired',
  withdrawn: 'expired',
  refused: 'refused',
};

export const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** Pan bounds at a scale: the image's edges never come inside the stage. */
export function clampPan(
  pan: { x: number; y: number },
  scale: number,
  size: { width: number; height: number },
): { x: number; y: number } {
  const bx = ((scale - 1) * size.width) / 2;
  const by = ((scale - 1) * size.height) / 2;
  return { x: clamp(pan.x, -bx, bx), y: clamp(pan.y, -by, by) };
}

/** Zooming to `next` around a point keeps that point still. Point is relative to the centre. */
export function zoomAround(
  scale: number,
  pan: { x: number; y: number },
  next: number,
  point: { x: number; y: number },
): { scale: number; pan: { x: number; y: number } } {
  const s = clamp(next, 1, ZOOM_MAX);
  const k = s / scale;
  return {
    scale: s,
    pan:
      s === 1
        ? { x: 0, y: 0 }
        : { x: point.x - (point.x - pan.x) * k, y: point.y - (point.y - pan.y) * k },
  };
}

/** A drag at the ends of the sequence gives, with resistance, instead of moving freely. */
export function dragOffset(dx: number, index: number, count: number): number {
  const atStart = index === 0 && dx > 0;
  const atEnd = index === count - 1 && dx < 0;
  return atStart || atEnd ? dx * EDGE_RESISTANCE : dx;
}

/** Where a horizontal release goes: -1 previous, 1 next, 0 back. */
export function navRelease(
  dx: number,
  velocity: number,
  width: number,
  index: number,
  count: number,
): -1 | 0 | 1 {
  const far = Math.abs(dx) > width * NAV_SHARE || Math.abs(velocity) > FLICK;
  if (!far) return 0;
  if (dx < 0 && index < count - 1) return 1;
  if (dx > 0 && index > 0) return -1;
  return 0;
}

/** Whether a downward pull dismisses. */
export const dismissRelease = (dy: number, velocity: number) =>
  dy > DISMISS_PX || (dy > 0 && velocity > FLICK);
